<template>
  <Modal
    v-if="modals.reminder && availableReminders.length && players[playerIndex]"
    @close="toggleModal('reminder')"
  >
    <!-- Custom reminder input view -->
    <template v-if="showCustomInput">
      <h3>{{ $t("modals.reminder.addCustomReminder") }}</h3>
      <div class="input-group">
        <input
          type="text"
          v-model="customReminderText"
          :placeholder="$t('modals.reminder.addCustomReminder')"
          maxlength="100"
          @keyup.enter="saveCustomReminder"
          ref="customInput"
        />
      </div>
      <div class="button-group">
        <div class="button save" @click="saveCustomReminder">
          {{ $t("common.save") }}
        </div>
        <div class="button cancel" @click="cancelCustomReminder">
          {{ $t("common.cancel") }}
        </div>
      </div>
    </template>
    
    <!-- Reminder selection view -->
    <template v-else>
      <h3>{{ $t("modals.reminder.chooseReminder") }}</h3>
      <ul class="reminders">
        <li
          v-for="reminder in availableReminders"
          class="reminder"
          :class="[reminder.role]"
          :key="reminder.role + ' ' + reminder.name"
          @click="addReminder(reminder)"
        >
          <span
            class="icon"
            :style="{
              backgroundImage: `url(${
                reminder.image && grimoire.isImageOptIn
                  ? reminder.image
                  : require(
                      '../../assets/icons/' +
                        (reminder.imageAlt || reminder.role) +
                        '.png',
                    )
              })`,
            }"
          ></span>
          <span class="text">{{ reminder.name }}</span>
        </li>
      </ul>
    </template>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapMutations, mapState } from "vuex";

/**
 * Helper function that maps a reminder name with a role-based object that provides necessary visual data.
 * @param role The role for which the reminder should be generated
 * @return {function(*): {image: string|string[]|string|*, role: *, name: *, imageAlt: string|*}}
 */
const mapReminder =
  ({ id, image, imageAlt }) =>
  (name) => ({
    role: id,
    image,
    imageAlt,
    name,
  });

export default {
  components: { Modal },
  props: ["playerIndex"],
  computed: {
    availableReminders() {
      let reminders = [];
      const { players, bluffs } = this.$store.state.players;
      this.$store.state.roles.forEach((role) => {
        // add reminders from player roles
        if (players.some((p) => p.role.id === role.id)) {
          reminders = [...reminders, ...role.reminders.map(mapReminder(role))];
        }
        // add reminders from bluff/other roles
        else if (bluffs.some((bluff) => bluff.id === role.id)) {
          reminders = [...reminders, ...role.reminders.map(mapReminder(role))];
        }
        // add global reminders
        if (role.remindersGlobal && role.remindersGlobal.length) {
          reminders = [
            ...reminders,
            ...role.remindersGlobal.map(mapReminder(role)),
          ];
        }
      });
      // add fabled reminders
      this.$store.state.players.fabled.forEach((role) => {
        // Skip roles without reminders (e.g., storyteller)
        if (role.reminders && role.reminders.length) {
          reminders = [...reminders, ...role.reminders.map(mapReminder(role))];
        }
      });

      // add out of script traveler reminders
      this.$store.state.otherTravelers.forEach((role) => {
        if (players.some((p) => p.role.id === role.id)) {
          reminders = [...reminders, ...role.reminders.map(mapReminder(role))];
        }
      });

      reminders.push({ role: "good", name: this.$t("modals.reminder.good") });
      reminders.push({ role: "evil", name: this.$t("modals.reminder.evil") });
      reminders.push({
        role: "custom",
        name: this.$t("modals.reminder.customNote"),
      });
      return reminders;
    },
    ...mapState(["modals", "grimoire"]),
    ...mapState("players", ["players"]),
  },
  data() {
    return {
      showCustomInput: false,
      customReminderText: "",
    };
  },
  methods: {
    addReminder(reminder) {
      if (reminder.role === "custom") {
        // Show custom input modal instead of using prompt()
        this.showCustomInput = true;
        this.customReminderText = "";
        this.$nextTick(() => {
          if (this.$refs.customInput) {
            this.$refs.customInput.focus();
          }
        });
        return;
      }
      
      const player = this.$store.state.players.players[this.playerIndex];
      const value = [...player.reminders, reminder];
      this.$store.commit("players/update", {
        player,
        property: "reminders",
        value,
      });
      this.$store.commit("toggleModal", "reminder");
    },
    saveCustomReminder() {
      const name = this.customReminderText.trim();
      if (!name) return;
      
      const player = this.$store.state.players.players[this.playerIndex];
      const value = [...player.reminders, { role: "custom", name }];
      this.$store.commit("players/update", {
        player,
        property: "reminders",
        value,
      });
      
      this.showCustomInput = false;
      this.customReminderText = "";
      this.$store.commit("toggleModal", "reminder");
    },
    cancelCustomReminder() {
      this.showCustomInput = false;
      this.customReminderText = "";
    },
    ...mapMutations(["toggleModal"]),
  },
};
</script>

<style scoped lang="scss">
.input-group {
  margin: 20px 0;

  input {
    width: 100%;
    padding: 12px 16px;
    font-size: 16px;
    border: none;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.95);
    color: #333;
    box-sizing: border-box;

    &:focus {
      outline: none;
      background: white;
      box-shadow: 0 0 10px rgba(0, 123, 255, 0.5);
    }

    &::placeholder {
      color: #999;
    }
  }
}

.button-group {
  display: flex;
  gap: 15px;
  margin-top: 20px;

  .button {
    flex: 1;
    padding: 12px 20px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

    &.save {
      background: linear-gradient(180deg, #4a90e2 0%, #357abd 100%);
      color: white;

      &:hover {
        background: linear-gradient(180deg, #5ba3ff 0%, #4a90e2 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
      }
    }

    &.cancel {
      background: linear-gradient(180deg, #e74c3c 0%, #c0392b 100%);
      color: white;

      &:hover {
        background: linear-gradient(180deg, #ff6b5a 0%, #e74c3c 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
      }
    }
  }
}

ul.reminders .reminder {
  background: url("../../assets/reminder.png") center center;
  background-size: 100%;
  width: 14vh;
  height: 14vh;
  max-width: 100px;
  max-height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1%;

  border-radius: 50%;
  border: 3px solid black;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  line-height: 100%;
  transition: transform 500ms ease;

  .icon {
    position: absolute;
    top: 0;
    width: 90%;
    height: 90%;
    background-size: 100%;
    background-position: center center;
    background-repeat: no-repeat;
  }

  .text {
    color: black;
    font-size: 65%;
    font-weight: bold;
    text-align: center;
    top: 28%;
    width: 80%;
    line-height: 1;
  }

  &:hover {
    transform: scale(1.2);
  }
}
</style>
